/**
 * Keyboard, mouse and gamepad -> one `Command` per sim tick (06 §10). This file is the browser
 * shell: it listens, swallows the keys the game has bound, and turns the cursor into a ground
 * point. What the inputs MEAN - bindings, schemes, toggles, weapon cycling, the quick knife, the
 * classic 32-step aim, the pad - lives in `core.ts` and its pure helpers.
 *
 * Modern scheme: movement is relative to the screen and the mouse (or the right stick) aims at a
 * point on the ground. Classic scheme: see classicAim.ts.
 */

import type { PixelCamera } from '../render/camera/PixelCamera';
import { VIEW_W } from '../render/camera/PixelCamera';
import type { Command, WeaponId } from '../sim/types';
import { type AimBody, groundFactor } from './aimPick';
import type { AimVolume } from './aimVolume';
import { type Bindings, getBindings } from './bindings';
import { InputCore, type InputOptions } from './core';
import { pollPad } from './padPoll';

export type { InputOptions } from './core';

export interface ViewRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Focus is on a control that wants the keyboard for itself (a HUD overlay button). */
function onControl(target: EventTarget | null): boolean {
  const tag = (target as { tagName?: unknown } | null)?.tagName;
  return tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
}

export class Input {
  private readonly core: InputCore;
  private mouseX = 0;
  private mouseY = 0;
  private targets: readonly AimBody[] = [];
  private volumes: readonly AimVolume[] = [];
  private readonly dispose: Array<() => void> = [];

  /** `bindings` defaults to the store the menus' Controls screen edits. */
  constructor(private readonly target: HTMLElement, bindings: Bindings = getBindings()) {
    const core = new InputCore(bindings);
    this.core = core;
    const on = <K extends keyof WindowEventMap>(
      type: K,
      fn: (e: WindowEventMap[K]) => void,
      options?: AddEventListenerOptions,
    ): void => {
      window.addEventListener(type, fn, options);
      this.dispose.push(() => window.removeEventListener(type, fn, options));
    };
    on('keydown', (e) => {
      if (e.repeat) {
        if (bindings.lookup(e.code) !== null && !onControl(e.target)) e.preventDefault();
        return;
      }
      // A focused overlay button keeps Space and Enter; Pause still reaches the game.
      if (onControl(e.target) && bindings.lookup(e.code) !== 'pause') return;
      // Bound keys are the game's: Tab, Space, the arrows, F5 and Alt must not reach the browser.
      if (core.keyDown(e.code) !== null) e.preventDefault();
    });
    on('keyup', (e) => {
      if (bindings.lookup(e.code) !== null && !onControl(e.target)) e.preventDefault();
      core.keyUp(e.code);
    });
    on('blur', () => core.releaseAll());
    on('mousemove', (e) => {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
      core.pointerUsed();
    });
    on('mousedown', (e) => {
      if (e.target !== this.target) return;
      if (core.keyDown(`Mouse${e.button}`) !== null) e.preventDefault();
    });
    on('mouseup', (e) => core.keyUp(`Mouse${e.button}`));
    on('contextmenu', (e) => e.preventDefault());
    on('wheel', (e) => {
      if (e.deltaY !== 0) core.tap(e.deltaY > 0 ? 'WheelDown' : 'WheelUp');
    }, { passive: true });
  }

  /** Run, crawl and sneak as toggles or held (08 §4). */
  setOptions(options: Partial<InputOptions>): void {
    this.core.setOptions(options);
  }

  /**
   * Tell the input layer the ranger is in the aircraft or under the canopy (`insertion.phase` is
   * not `ground`): the classic scheme then steers directly instead of tapping and turning.
   */
  setAirborne(airborne: boolean): void {
    this.core.setAirborne(airborne);
  }

  /**
   * ROUND7 §2: the men the cursor can land on this tick (`aimTargets`). A cursor on one of them aims every weapon
   * at his feet, since the ground under it is behind him. Call before each `command`.
   */
  setAimTargets(targets: readonly AimBody[]): void {
    this.targets = targets;
  }

  /** The aim audit: the solids the cursor's line of sight can meet this tick (`app/aimVolumes.ts`). */
  setAimVolumes(volumes: readonly AimVolume[]): void {
    this.volumes = volumes;
  }

  /** True once per press of Pause: the bound key (Esc) or the pad's Start. Call every frame. */
  consumePause(): boolean {
    return this.core.consumePause(pollPad());
  }

  /** The crawl toggle is input-side state; a downed ranger's forced crawl is the sim's. */
  get crawling(): boolean {
    return this.core.crawling;
  }

  /** The map / tactical-view control is down (Tab, or Select on the pad). */
  get mapHeld(): boolean {
    return this.core.mapHeld;
  }

  /**
   * One sim tick's command. `available` is what next / previous weapon may land on
   * (`availableWeapons(ranger)`); without it they cycle the five standard weapons.
   */
  command(
    rangerX: number,
    rangerZ: number,
    current: WeaponId,
    camera: PixelCamera,
    view: ViewRect,
    available?: readonly WeaponId[],
  ): Command {
    // Mouse -> internal texels -> the ground point under the cursor.
    const texel = view.width / VIEW_W;
    const pointer = camera.screenToGround((this.mouseX - view.left) / texel, (this.mouseY - view.top) / texel);
    return this.core.step({
      rangerX, rangerZ, current, available, pointer, pad: pollPad(),
      // The camera's own pitch (the game flies 45 deg, the class defaults to 55): never assumed here.
      targets: this.targets, volumes: this.volumes, groundFactor: groundFactor(camera),
    });
  }

  destroy(): void {
    for (const off of this.dispose) off();
    this.dispose.length = 0;
  }
}
